/**
 * Porównanie faktury z cennikiem (NX-417) — klucze `ivc.*`. Scalane przez `extra.ts`; `ivcEn` typowane względem `ivcPl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const ivcPl = {
  title: 'Porównanie faktury',
  titleOwn: 'Reklamacja ceny — porównanie faktury',
  subtitle: 'Pozycje z faktury odczytane przez AI, nasz towar i cena dla klienta z cennika Subiekta.',
  compare: 'Porównaj fakturę',
  compareWithPrices: 'Porównaj z cennikiem',
  uploadHint: 'Zdjęcie albo PDF faktury (JPG, PNG, WEBP, PDF, do 10 MB).',
  reading: 'Odczytuję fakturę…',
  readingHint: 'AI czyta pozycje faktury — zwykle 10–20 sekund.',
  readFailed: 'Nie udało się porównać faktury.',
  badType: 'Dodaj zdjęcie (JPG, PNG, WEBP) albo PDF.',
  tooLarge: 'Plik jest za duży (najwyżej 10 MB).',
  mock: 'Tryb demonstracyjny: zamiast odczytu przez AI pokazana jest przykładowa faktura.',
  aiCost: 'Odczyt: {model}, {seconds} s',
  back: 'Wróć',
  mode: {
    COMPETITOR: 'Faktura innego dostawcy',
    OWN_INVOICE: 'Nasza faktura — reklamacja ceny',
    switchToOwn: 'To nasza faktura',
    switchToCompetitor: 'To faktura innego dostawcy',
  },
  customer: {
    label: 'Klient',
    prospect: 'Prospekt — cena dla nowego klienta',
    prospectHint: 'Klienta nie ma w NEXOPS: liczymy cenę bez rabatu i cen indywidualnych (poziom domyślny, a bez niego mediana 30 dni).',
    suggested: 'Dopasowano po NIP nabywcy',
    useSuggested: 'Użyj: {name}',
    change: 'Zmień klienta',
    search: 'Szukaj klienta (nazwa, NIP)…',
    setProspect: 'Bez klienta (prospekt)',
    none: 'Brak klientów w Twoim zakresie.',
  },
  head: {
    seller: 'Sprzedawca',
    buyer: 'Nabywca',
    invoice: 'Faktura {number} z {date}',
    priceDate: 'Ceny na dzień {date}',
  },
  tile: {
    invoiceValue: 'Pozycje pewne — wg faktury',
    ourValue: 'U nas',
    ourValueOwn: 'Wg ceny klienta',
    savings: 'Oszczędność klienta',
    complaint: 'Różnica do reklamacji',
    positions: 'Pozycje pewne',
    positionsOf: '{matched} z {total}',
    toConfirm: 'do potwierdzenia: {count}',
    unmatched: 'bez dopasowania: {count}',
  },
  warning: {
    TOTALS_MISMATCH: 'Suma pozycji ({lines}) różni się od sumy netto faktury ({total}) — sprawdź odczyt.',
    NOT_AN_INVOICE: 'Plik nie wygląda na fakturę — sprawdź, czy dodano właściwy dokument.',
    NO_LINES: 'Nie odczytano żadnej pozycji — zrób wyraźniejsze zdjęcie.',
    FOREIGN_CURRENCY: 'Faktura nie jest w złotych — nasze ceny są w PLN.',
    BUYER_NIP_UNMATCHED: 'NIP nabywcy na fakturze jest inny niż NIP wybranego klienta.',
  },
  col: {
    line: 'Pozycja z faktury',
    product: 'Nasz towar',
    qty: 'Ilość',
    invoicePrice: 'Cena na fakturze',
    ourPrice: 'Nasza cena',
    source: 'Źródło ceny',
    diffUnit: 'Różnica / j.m.',
    diffTotal: 'Oszczędność',
    diffTotalOwn: 'Reklamacja',
  },
  confidence: { SURE: 'Pewne', TO_CONFIRM: 'Do potwierdzenia', NONE: 'Brak dopasowania' },
  note: {
    UNIT_FACTOR_MISSING: 'Podaj przelicznik jednostki',
    NO_PRICE: 'Brak naszej ceny',
    NO_QUANTITY: 'Brak ilości na fakturze',
    NO_INVOICE_PRICE: 'Brak ceny na fakturze',
  },
  perUnit: 'za {unit}',
  factorShort: '1 {from} = {factor} {to}',
  match: {
    title: 'Dopasuj towar',
    lineFromInvoice: 'Z faktury: {name} — {qty} {unit}',
    confirm: 'Potwierdź',
    change: 'Zmień',
    candidates: 'Propozycje',
    search: 'Szukaj towaru (nazwa, symbol)…',
    factor: 'Ile {unit} to 1 {invoiceUnit} z faktury?',
    factorHint: 'Np. 1 karton = 5 kg → wpisz 5. Przelicznik zostanie zapamiętany dla tego dostawcy.',
    save: 'Zapisz i zapamiętaj',
    none: 'Nie mamy takiego towaru',
    saved: 'Dopasowanie zapamiętane dla tego dostawcy.',
    remembered: 'zapamiętane',
  },
  export: {
    xlsx: 'Excel',
    pdf: 'PDF porównania',
    proposal: 'Propozycja cenowa (PDF)',
    proposalHint: 'Dla klienta: nasze towary, ceny i łączna oszczędność — bez nazwy i cen konkurencji.',
    file: 'Plik faktury',
  },
  list: {
    tab: 'Faktury',
    date: 'Data faktury',
    title: 'Porównania faktur',
    description: 'Faktury od innych dostawców porównane z cenami tego klienta.',
    empty: 'Brak porównań. Dodaj zdjęcie lub PDF faktury, którą klient dostał od innego dostawcy.',
    lines: '{count} poz.',
  },
  remove: 'Usuń porównanie',
  removeConfirm: 'Usunąć to porównanie razem z plikiem faktury?',
  removed: 'Porównanie usunięte.',
  ai: {
    attach: 'Dodaj fakturę do porównania z cennikiem',
    userMessage: 'Porównaj fakturę z cennikiem: {name}',
    summary: '**Faktura {number}** — {seller}, {lines} poz.\n\nKlient: {customer}. Pewne: {matched}, do potwierdzenia: {toConfirm}, bez dopasowania: {unmatched}.\n\n{result}\n\n[Otwórz pełne porównanie]({link})',
    cheaper: 'Na pozycjach pewnych klient zapłaciłby u nas **{amount}** mniej ({pct}).',
    dearer: 'Na pozycjach pewnych u nas byłoby **{amount}** drożej ({pct}).',
    own: 'To nasza faktura: różnica względem ceny klienta na pozycjach pewnych to **{amount}**.',
    noneSure: 'Brak pozycji pewnych — potwierdź dopasowania w pełnym porównaniu, żeby policzyć oszczędność.',
    prospect: 'prospekt (cena dla nowego klienta)',
  },
};

export const ivcEn: Shape<typeof ivcPl> = {
  title: 'Invoice comparison',
  titleOwn: 'Price complaint — invoice comparison',
  subtitle: 'Invoice lines read by AI, our product and the customer price from the Subiekt price list.',
  compare: 'Compare invoice',
  compareWithPrices: 'Compare with price list',
  uploadHint: 'Photo or PDF of the invoice (JPG, PNG, WEBP, PDF, up to 10 MB).',
  reading: 'Reading the invoice…',
  readingHint: 'AI reads the invoice lines — usually 10–20 seconds.',
  readFailed: 'Could not compare the invoice.',
  badType: 'Add a photo (JPG, PNG, WEBP) or a PDF.',
  tooLarge: 'The file is too large (max 10 MB).',
  mock: 'Demo mode: a sample invoice is shown instead of an AI read.',
  aiCost: 'Read: {model}, {seconds} s',
  back: 'Back',
  mode: {
    COMPETITOR: 'Invoice of another supplier',
    OWN_INVOICE: 'Our invoice — price complaint',
    switchToOwn: 'This is our invoice',
    switchToCompetitor: 'This is another supplier’s invoice',
  },
  customer: {
    label: 'Customer',
    prospect: 'Prospect — new customer price',
    prospectHint: 'The customer is not in NEXOPS: price without discount or individual prices (default level, else the 30-day median).',
    suggested: 'Matched by the buyer’s tax ID',
    useSuggested: 'Use: {name}',
    change: 'Change customer',
    search: 'Search customer (name, tax ID)…',
    setProspect: 'No customer (prospect)',
    none: 'No customers in your scope.',
  },
  head: {
    seller: 'Seller',
    buyer: 'Buyer',
    invoice: 'Invoice {number} of {date}',
    priceDate: 'Prices as of {date}',
  },
  tile: {
    invoiceValue: 'Sure lines — per invoice',
    ourValue: 'With us',
    ourValueOwn: 'At customer price',
    savings: 'Customer saving',
    complaint: 'Difference to complain',
    positions: 'Sure lines',
    positionsOf: '{matched} of {total}',
    toConfirm: 'to confirm: {count}',
    unmatched: 'unmatched: {count}',
  },
  warning: {
    TOTALS_MISMATCH: 'The line total ({lines}) differs from the invoice net total ({total}) — check the reading.',
    NOT_AN_INVOICE: 'The file does not look like an invoice — check the document.',
    NO_LINES: 'No lines were read — take a sharper photo.',
    FOREIGN_CURRENCY: 'The invoice is not in PLN — our prices are in PLN.',
    BUYER_NIP_UNMATCHED: 'The buyer’s tax ID differs from the selected customer’s.',
  },
  col: {
    line: 'Invoice line',
    product: 'Our product',
    qty: 'Quantity',
    invoicePrice: 'Invoice price',
    ourPrice: 'Our price',
    source: 'Price source',
    diffUnit: 'Difference / unit',
    diffTotal: 'Saving',
    diffTotalOwn: 'Complaint',
  },
  confidence: { SURE: 'Sure', TO_CONFIRM: 'To confirm', NONE: 'No match' },
  note: {
    UNIT_FACTOR_MISSING: 'Enter the unit conversion',
    NO_PRICE: 'No price of ours',
    NO_QUANTITY: 'No quantity on the invoice',
    NO_INVOICE_PRICE: 'No price on the invoice',
  },
  perUnit: 'per {unit}',
  factorShort: '1 {from} = {factor} {to}',
  match: {
    title: 'Match product',
    lineFromInvoice: 'From the invoice: {name} — {qty} {unit}',
    confirm: 'Confirm',
    change: 'Change',
    candidates: 'Suggestions',
    search: 'Search product (name, code)…',
    factor: 'How many {unit} is 1 {invoiceUnit} on the invoice?',
    factorHint: 'E.g. 1 carton = 5 kg → enter 5. Remembered for this supplier.',
    save: 'Save and remember',
    none: 'We do not have this product',
    saved: 'Match remembered for this supplier.',
    remembered: 'remembered',
  },
  export: {
    xlsx: 'Excel',
    pdf: 'Comparison PDF',
    proposal: 'Price proposal (PDF)',
    proposalHint: 'For the customer: our products, prices and the total saving — no competitor name or prices.',
    file: 'Invoice file',
  },
  list: {
    tab: 'Invoices',
    date: 'Invoice date',
    title: 'Invoice comparisons',
    description: 'Invoices from other suppliers compared with this customer’s prices.',
    empty: 'No comparisons. Add a photo or PDF of an invoice the customer got from another supplier.',
    lines: '{count} lines',
  },
  remove: 'Delete comparison',
  removeConfirm: 'Delete this comparison with the invoice file?',
  removed: 'Comparison deleted.',
  ai: {
    attach: 'Add an invoice to compare with our prices',
    userMessage: 'Compare the invoice with our prices: {name}',
    summary: '**Invoice {number}** — {seller}, {lines} lines.\n\nCustomer: {customer}. Sure: {matched}, to confirm: {toConfirm}, unmatched: {unmatched}.\n\n{result}\n\n[Open the full comparison]({link})',
    cheaper: 'On the sure lines the customer would pay **{amount}** less with us ({pct}).',
    dearer: 'On the sure lines we would be **{amount}** more expensive ({pct}).',
    own: 'This is our invoice: the difference from the customer price on the sure lines is **{amount}**.',
    noneSure: 'No sure lines — confirm the matches in the full comparison to compute the saving.',
    prospect: 'prospect (new customer price)',
  },
};
